// Zero-scroll visual planning workspace pieces for the Container Planner.
// Dark "tactical" dashboard: interactive Top View (drop = +1 pallet), interactive
// Elevation views (per-box ✕ = -1 pallet), pinned telemetry rail, bottom catalog tray.
import { useState, useMemo } from "react";
import {
  Search, Plus, Package, PackageX, AlertTriangle, CheckCircle2, TrendingDown, Gauge, Scale3d, X, Trash2,
} from "lucide-react";
import { topdownSvg, sideSvg } from "./loadDiagram";
import { SplitBar, WeightLbsKg, KG_TO_LB } from "./planQualityUI";
import { SandboxElevationPanel } from "./sandboxElevation";
import { computeUsAxles, computeUsDecisionSuite, DECISION_SUITE_LABEL, COGX } from "../lib/truckSpecs";

const STATUS = {
  fits: { label: "FITS CAPACITY", cls: "bg-emerald-500/10 border-emerald-500/40 text-emerald-400", Icon: CheckCircle2 },
  under_loaded: { label: "UNDER-LOADED", cls: "bg-amber-500/10 border-amber-500/40 text-amber-400", Icon: TrendingDown },
  over_loaded: { label: "OVER CAPACITY", cls: "bg-rose-500/10 border-rose-500/40 text-rose-400", Icon: AlertTriangle },
};

// US Highway Compliance (BETA) — Scale Ticket widget shown in place of the standard
// FHWA 60/40 axle-balance bar when `us_highway_mode` is ON. Every axle row shows
// LBS + KG side-by-side; any breach of the 12k / 34k / 34k / 80k lb FHWA limits
// turns RED with a specific "over by N lb" callout so the loader knows which axle
// to shed. Matches the physics engine in container_planner_service.us_highway_axles.
function AxleRow({ label, actual_lb, actual_kg, limit_lb, limit_kg, over_lb, ok, testid }) {
  const pct = limit_lb > 0 ? Math.min(120, (actual_lb / limit_lb) * 100) : 0;
  return (
    <div className="rounded border border-slate-800 bg-slate-950/50 p-2" data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px] font-semibold text-slate-200">{label}</span>
        <span className={`text-[9px] font-mono font-bold px-1.5 py-0.5 rounded ${ok ? "bg-emerald-500/15 text-emerald-400" : "bg-rose-500/25 text-rose-300"}`} data-testid={`${testid}-status`}>
          {ok ? "OK" : `OVER +${Math.round(over_lb).toLocaleString()} lbs`}
        </span>
      </div>
      {/* amber lbs · sky kg — matches Payload Utilisation and US Decision Suite */}
      <div className="flex items-baseline justify-between font-mono">
        <span className={`text-[13px] font-bold tabular-nums ${ok ? "text-amber-500" : "text-rose-300"}`} data-testid={`${testid}-actual-lb`}>
          {Math.round(actual_lb).toLocaleString()}
          <span className="text-[9px] uppercase tracking-wider opacity-80 ml-1">lbs</span>
        </span>
        <span className={`text-[11px] tabular-nums ${ok ? "text-sky-500" : "text-rose-300/80"}`} data-testid={`${testid}-actual-kg`}>
          {Math.round(actual_kg).toLocaleString()}
          <span className="text-[9px] uppercase tracking-wider opacity-80 ml-1">kg</span>
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden mt-1">
        <div className={`h-full transition-all duration-300 ${ok ? (pct > 85 ? "bg-amber-500" : "bg-emerald-500") : "bg-rose-500"}`} style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
      <div className="mt-1 text-[9px] font-mono text-slate-500 flex items-center justify-between">
        <span>Limit</span>
        <span className="tabular-nums">
          <span className="text-amber-500/80">{Math.round(limit_lb).toLocaleString()}<span className="ml-0.5 opacity-80">lbs</span></span>
          <span className="mx-1 text-slate-600">·</span>
          <span className="text-sky-500/80">{Math.round(limit_kg).toLocaleString()}<span className="ml-0.5 opacity-80">kg</span></span>
        </span>
      </div>
    </div>
  );
}

function UsScaleTicket({ us, plan, truckSpecs }) {
  // Live client-side recompute: whenever the admin edits Truck Setup, the Scale
  // Ticket refreshes instantly against the same placement the backend used —
  // no plan re-run required. Falls back to the baked-in `us` when no placement.
  const live = useMemo(() => {
    if (!truckSpecs || !plan?.placement?.length) return null;
    try { return computeUsAxles(plan.placement, truckSpecs); } catch { return null; }
  }, [plan, truckSpecs]);
  const uu = live || us;
  if (!uu) {
    return (
      <div className="rounded-lg border border-orange-800/60 bg-orange-950/20 p-3" data-testid="indicator-us-scale-ticket">
        <div className="flex items-center gap-1.5 mb-2">
          <Scale3d className="w-4 h-4 text-orange-400" />
          <span className="text-[10px] font-mono uppercase tracking-wider text-orange-300">US Scale Ticket <span className="text-[9px] font-bold px-1 py-0.5 rounded bg-orange-500/25 text-orange-200 ml-1">BETA</span></span>
        </div>
        <div className="text-[11px] text-slate-400">Load a plan to see the Law-of-Levers axle read-out.</div>
      </div>
    );
  }
  const compliant = uu.compliant !== false;
  const c = uu.constants || {};
  return (
    <div className={`rounded-lg border p-3 ${compliant ? "border-orange-800/60 bg-orange-950/20" : "border-rose-500/60 bg-rose-950/30"}`} data-testid="indicator-us-scale-ticket">
      <div className="flex items-center gap-1.5 mb-2">
        <Scale3d className={`w-4 h-4 ${compliant ? "text-orange-400" : "text-rose-400"}`} />
        <span className={`text-[10px] font-mono uppercase tracking-wider ${compliant ? "text-orange-300" : "text-rose-300"}`}>
          US Scale Ticket <span className="text-[9px] font-bold px-1 py-0.5 rounded bg-orange-500/25 text-orange-200 ml-1">BETA</span>
        </span>
        <span className={`ml-auto text-[9px] font-mono font-bold px-1.5 py-0.5 rounded ${compliant ? "bg-emerald-500/20 text-emerald-300" : "bg-rose-500/25 text-rose-200"}`} data-testid="us-scale-ticket-overall">
          {compliant ? "COMPLIANT" : "OVERLOAD"}
        </span>
      </div>
      <div className="space-y-1.5">
        <AxleRow label="Steer axle" {...uu.steer} testid="us-axle-steer" />
        <AxleRow label="Drive tandem" {...uu.drive} testid="us-axle-drive" />
        <AxleRow label="Trailer tandem" {...uu.trailer} testid="us-axle-trailer" />
        <AxleRow label="Gross Vehicle Weight (GVW)" {...uu.gvw} testid="us-axle-gvw" />
      </div>
      {!compliant && (uu.violations || []).length > 0 && (
        <div className="mt-2 rounded border border-rose-500/40 bg-rose-500/10 px-2 py-1.5" data-testid="us-scale-ticket-violations">
          <div className="flex items-start gap-1.5 text-[10px] text-rose-300">
            <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
            <div className="space-y-0.5">
              {uu.violations.map((v, i) => (
                <div key={i} data-testid={`us-violation-${v.axle}`}>{v.message}</div>
              ))}
            </div>
          </div>
        </div>
      )}
      <div className="mt-2 text-[9px] text-slate-500 font-mono leading-relaxed" data-testid="us-scale-ticket-constants">
        Law of Levers · Steer tare {(c.tare_steer_lb || 0).toLocaleString()} · Drive tare {(c.tare_drive_lb || 0).toLocaleString()} · Trailer tare {(c.tare_trailer_lb || 0).toLocaleString()}
        <br />
        5th-wheel lead <span data-testid="us-constant-fifth-wheel-lead" className="text-orange-300 font-bold">{Number(c.fifth_wheel_lead_in || 0).toFixed(1)}&quot;</span> · Wheelbase {Number(c.tractor_wheelbase_in || 0).toFixed(0)}&quot; · Axle span {Number(c.axle_span_in || 0).toFixed(0)}&quot; · Overhang {Number(c.front_overhang_in || 0).toFixed(0)}&quot;
      </div>
    </div>
  );
}

// Interactive top-down floor plan — drop target for catalog products (+1 pallet) AND for
// unassigned-pool chips (drops the deferred pallet back into the active container).
// Click / double-click a stack (or its ↻ badge) to rotate the ENTIRE column 90°.
export function TopViewPanel({ plan, cmap, onDropProduct, onDropPool, onRotateStack }) {
  const [over, setOver] = useState(false);
  const cont = plan?.container || {};
  const cl = cont.internal_length_in || 1, cw = cont.internal_width_in || 1;
  const svg = plan?.placement?.length
    ? topdownSvg(plan.placement, cmap, cl, cw, { svgClass: "w-full h-full block" })
    : null;
  const handleDrop = (e) => {
    e.preventDefault(); setOver(false);
    try {
      const d = JSON.parse(e.dataTransfer.getData("text/plain"));
      if (d?.source === "pool" && d.product_code) onDropPool?.(d.product_code, d.kind || "po");
      else if (d?.product_code) onDropProduct(d.product_code, d.ppp || 0, d.description || "");
    } catch { /* ignore malformed drag payload */ }
  };
  // Rotate on: (a) click on the corner ↻ pill (data-rotate-lane), or (b) double-click anywhere
  // on the pallet group (data-lane). Single-click on the body is reserved (may seed drag),
  // so the pill is the primary trigger — the tooltip advertises both gestures.
  const _resolveRotate = (target) => {
    const pill = target.closest?.("[data-rotate-lane]");
    if (pill) return { lane: pill.getAttribute("data-rotate-lane"), pos: pill.getAttribute("data-rotate-pos") };
    const grp = target.closest?.("[data-lane]");
    if (grp) return { lane: grp.getAttribute("data-lane"), pos: grp.getAttribute("data-pos") };
    return null;
  };
  const handleClick = (e) => {
    if (!onRotateStack) return;
    // pill click always rotates (small target = intentional)
    if (e.target.closest?.("[data-rotate-lane]")) {
      const r = _resolveRotate(e.target);
      if (r && r.lane != null && r.pos !== "" && r.pos != null) onRotateStack(r.lane, parseInt(r.pos, 10));
    }
  };
  const handleDblClick = (e) => {
    if (!onRotateStack) return;
    const r = _resolveRotate(e.target);
    if (r && r.lane != null && r.pos !== "" && r.pos != null) onRotateStack(r.lane, parseInt(r.pos, 10));
  };
  return (
    <div data-testid="top-view-drop-target"
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={handleDrop}
      className={`relative flex-1 min-h-0 rounded-lg border p-2 flex flex-col transition-all ${over ? "border-2 border-dashed border-emerald-400 bg-emerald-500/10 shadow-[0_0_22px_rgba(16,185,129,0.28)]" : "border-slate-800 bg-slate-900/80"}`}>
      <div className="flex items-center justify-between mb-1 shrink-0">
        <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400">Top-down floor plan · front → rear</span>
        <span className="text-[10px] font-mono text-slate-500">drag a product = +1 pallet · click ↻ pill or dbl-click a stack to rotate 90°</span>
      </div>
      <div className="flex-1 min-h-0 w-full" onClick={handleClick} onDoubleClick={handleDblClick}>
        {svg
          ? <div className="h-full w-full" dangerouslySetInnerHTML={{ __html: svg }} />
          : <div className="h-full w-full flex items-center justify-center text-sm text-slate-500 border border-dashed border-slate-700 rounded">Empty container — drag a product from the tray to load it.</div>}
      </div>
      {over && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span className="px-3 py-1 rounded-full bg-emerald-500 text-white text-xs font-bold font-mono animate-pulse">DROP TO ADD +1 PALLET</span>
        </div>
      )}
    </div>
  );
}

// Interactive side elevation for one lane — each box carries a ✕ that removes 1 pallet.
// When `sandbox` is truthy, the panel becomes a Compliance Sandbox: every pallet
// layer is a draggable box (floor pallet = whole stack, upper layers = single box)
// and the panel is a drop target that fires `onSandboxMove` on every drop. The
// classic ✕ / ? interactions are hidden in that mode — dragging is the primary
// gesture and the physics telemetry reacts to every drop without any revert.
export function ElevationPanel({ plan, cmap, lane, onRemove, onAudit, testid, sandbox, onSandboxMove, cogx }) {
  if (sandbox) {
    return <SandboxElevationPanel plan={plan} cmap={cmap} lane={lane} onSandboxMove={onSandboxMove} testid={testid} />;
  }
  const cont = plan?.container || {};
  const cl = cont.internal_length_in || 1, ch = cont.internal_height_in || 1;
  const laneLabel = lane === "B" ? "Right Lane (from Rear)" : "Left Lane (from Rear)";
  const svg = plan?.placement?.length
    ? sideSvg(plan.placement, cmap, lane, cl, ch, { removable: true, auditable: !!onAudit, svgClass: "w-full h-full block" })
    : null;
  const onClick = (e) => {
    const a = e.target.closest("[data-audit-code]");
    if (a) {
      const code = a.getAttribute("data-audit-code");
      const auditLane = a.getAttribute("data-audit-lane") || lane;
      const base = (a.getAttribute("data-audit-base") || "").split("|").filter(Boolean);
      if (code && onAudit) onAudit(auditLane, base, code);
      return;
    }
    const g = e.target.closest("[data-remove-code]");
    if (!g) return;
    const code = g.getAttribute("data-remove-code");
    const isFiller = g.getAttribute("data-remove-filler") === "1";
    if (code) onRemove(code, isFiller);
  };
  return (
    <div className="relative flex-1 min-h-0 rounded-lg border border-slate-800 bg-slate-900/80 p-2 flex flex-col" data-testid={testid}>
      <div className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-1 shrink-0">
        Elevation · {laneLabel} <span className="text-slate-600 normal-case">— ✕ moves a pallet to Unassigned · <span className="text-blue-400">?</span> audits adding one more to that stack</span>
      </div>
      <div className="relative flex-1 min-h-0 w-full" onClick={onClick}>
        {svg
          ? <div className="h-full w-full" dangerouslySetInnerHTML={{ __html: svg }} />
          : <div className="h-full w-full flex items-center justify-center text-xs text-slate-600">No pallets loaded.</div>}
        {/* US Decision Suite: coloured CoG-X vertical line overlaid on the elevation.
            Absolute-positioned inside the SVG frame — no dependency on the SVG generator. */}
        {cogx && cogx.cog_x_pct > 0 && (
          <div
            className={`absolute top-0 bottom-0 w-[3px] pointer-events-none z-10 ${
              cogx.band === "green" ? "bg-emerald-400"
              : cogx.band === "amber" ? "bg-amber-400"
              : "bg-rose-500"}`}
            style={{ left: `${Math.min(100, Math.max(0, cogx.cog_x_pct))}%`,
                     boxShadow: "0 0 6px currentColor" }}
            data-testid={`elevation-cogx-line-${lane}`}
            data-cogx-pct={cogx.cog_x_pct}
            data-cogx-band={cogx.band}
            title={`CoG-X ${cogx.cog_x_pct}% (band ${cogx.band})`}
          >
            <span className={`absolute -top-0.5 left-1 text-[9px] font-mono font-bold px-1 rounded whitespace-nowrap ${
              cogx.band === "green" ? "bg-emerald-500 text-white"
              : cogx.band === "amber" ? "bg-amber-500 text-white"
              : "bg-rose-500 text-white"}`}>
              CoG-X {cogx.cog_x_pct}%
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// US Highway Decision Suite widget — CoG-X readout + illustrative two-scenario
// axle ranges + Copy Load Profile. Lives client-side so the read-out refreshes
// instantly on any Truck Setup save without a plan re-run.
function CogXBar({ cog }) {
  if (!cog || cog.band === "empty") return null;
  const clamp = (v) => Math.max(0, Math.min(100, v));
  const trackColor = cog.band === "green" ? "bg-emerald-400"
                    : cog.band === "amber" ? "bg-amber-400" : "bg-rose-500";
  return (
    <div className="relative h-6 mt-1" data-testid="decision-suite-cogx-bar">
      <div className="absolute inset-x-0 top-2 h-2 rounded-full bg-slate-800 overflow-hidden">
        {/* safe band 43-47 */}
        <div className="absolute top-0 h-full bg-emerald-500/40"
             style={{ left: `${clamp(cog.safe_low_pct)}%`, width: `${clamp(cog.safe_high_pct - cog.safe_low_pct)}%` }} />
        {/* absolute-window edges */}
        <div className="absolute top-0 h-full bg-amber-500/25"
             style={{ left: `${clamp(cog.abs_low_pct)}%`, width: `${clamp(cog.safe_low_pct - cog.abs_low_pct)}%` }} />
        <div className="absolute top-0 h-full bg-amber-500/25"
             style={{ left: `${clamp(cog.safe_high_pct)}%`, width: `${clamp(cog.abs_high_pct - cog.safe_high_pct)}%` }} />
      </div>
      <div className={`absolute top-0 h-full w-[3px] ${trackColor}`}
           style={{ left: `${clamp(cog.cog_x_pct)}%`, boxShadow: "0 0 6px currentColor" }} />
      <div className="absolute -top-1 flex items-center justify-between w-full text-[8px] font-mono text-slate-500 pointer-events-none">
        <span style={{ marginLeft: `${clamp(cog.target_pct) - 1.5}%` }} className="text-emerald-400">▼ 45%</span>
      </div>
    </div>
  );
}

function DecisionRangeRow({ axle, r }) {
  const testid = `decision-range-${axle}`;
  const rangeCls = r.any_over ? "text-rose-300" : "";
  return (
    <div className="grid grid-cols-4 items-center gap-2 text-[11px] py-1 border-t border-slate-800" data-testid={testid}>
      <span className="font-medium text-slate-300 capitalize">{axle === "gvw" ? "GVW" : axle}</span>
      <span className={`col-span-2 font-mono tabular-nums ${rangeCls}`} data-testid={`${testid}-range-lb`}>
        {/* amber lbs · sky kg — matches Payload Utilisation and US Axle Balance */}
        <span className={r.any_over ? "text-rose-300" : "text-amber-500"}>
          {r.min_lb.toLocaleString()}–{r.max_lb.toLocaleString()}
          <span className="text-[9px] uppercase tracking-wider opacity-80 ml-1">lbs</span>
        </span>
        <span className="mx-1.5 text-slate-500">·</span>
        <span className={r.any_over ? "text-rose-300/80" : "text-sky-500"} data-testid={`${testid}-range-kg`}>
          {r.min_kg.toLocaleString()}–{r.max_kg.toLocaleString()}
          <span className="text-[9px] uppercase tracking-wider opacity-80 ml-1">kg</span>
        </span>
      </span>
      <span className={`text-right text-[10px] font-mono ${r.any_over ? "text-rose-300 font-bold" : "text-slate-500"}`}
            data-testid={`${testid}-limit`}>
        {r.any_over ? `+${r.worst_over_lb.toLocaleString()} OVER` : `≤ ${r.limit_lb.toLocaleString()}`}
      </span>
    </div>
  );
}

function UsDecisionSuiteWidget({ plan, truckSpecs }) {
  // Live client-side recompute (mirrors backend). Falls back to plan.us_decision_suite
  // when there's no placement yet (setup / empty container).
  const suite = useMemo(() => {
    if (plan?.placement?.length && plan.container?.internal_length_in) {
      try {
        return computeUsDecisionSuite(plan.placement,
                                       plan.container.internal_length_in,
                                       truckSpecs);
      } catch { /* fall through */ }
    }
    return plan?.us_decision_suite || null;
  }, [plan, truckSpecs]);

  if (!suite) {
    return (
      <div className="rounded-lg border border-emerald-800/60 bg-emerald-950/20 p-3" data-testid="indicator-us-decision-suite">
        <div className="flex items-center gap-1.5 mb-1">
          <Scale3d className="w-4 h-4 text-emerald-400" />
          <span className="text-[10px] font-mono uppercase tracking-wider text-emerald-300">US Highway Decision Suite</span>
        </div>
        <div className="text-[11px] text-slate-400">Load a plan to compute CoG-X and illustrative axle ranges.</div>
      </div>
    );
  }

  const cog = suite.cog_x || {};
  const status = suite.status || "amber";
  const chipCls = status === "green" ? "bg-emerald-500 text-white"
                : status === "amber" ? "bg-amber-500 text-white"
                : "bg-rose-500 text-white";
  const borderCls = status === "green" ? "border-emerald-500/60 bg-emerald-950/30"
                  : status === "amber" ? "border-amber-500/60 bg-amber-950/30"
                  : "border-rose-500/60 bg-rose-950/30";

  const copyProfile = () => {
    const c = plan?.container || {};
    const rows = ["steer", "drive", "trailer", "gvw"].map((k) => {
      const r = suite.axle_ranges[k];
      return `  ${k.padEnd(7)} : ${r.min_lb.toLocaleString().padStart(6)}-${r.max_lb.toLocaleString().padStart(6)} lb  (${r.min_kg.toLocaleString()}-${r.max_kg.toLocaleString()} kg)${r.any_over ? "  *** OVER ***" : ""}`;
    });
    const text = [
      "US HIGHWAY DECISION SUITE — LOAD PROFILE",
      "="+"=".repeat(48),
      `Container: ${c.name || "?"}  (${c.internal_length_in || "?"}in L)`,
      `Total cargo: ${(plan?.total_weight_kg || 0).toLocaleString()} kg  ·  ${plan?.total_pallets || 0} pallets`,
      "",
      `CoG-X: ${cog.cog_x_pct}%  (${cog.cog_x_in} in from nose) — target 45%, safe ${cog.safe_low_pct}-${cog.safe_high_pct}%`,
      `Status: ${status.toUpperCase()}`,
      ...(suite.reasons || []).map((r) => `  · ${r}`),
      "",
      "Illustrative axle ranges (across two chassis scenarios):",
      ...rows,
      "",
      `Scenario A: ${suite.scenarios?.A?.label} — ${suite.scenarios?.A?.compliant ? "COMPLIANT" : "OVERLOAD"}`,
      `Scenario B: ${suite.scenarios?.B?.label} — ${suite.scenarios?.B?.compliant ? "COMPLIANT" : "OVERLOAD"}`,
      "",
      suite.illustrative_label || DECISION_SUITE_LABEL,
    ].join("\n");
    if (navigator.clipboard) navigator.clipboard.writeText(text).catch(() => {});
    // fire a global toast — sonner is available via a global window shim in the shell
    // eslint-disable-next-line no-alert
    try { (window.__sonner_toast || alert)("Load profile copied to clipboard"); }
    catch { alert("Copied"); }   // eslint-disable-line no-alert
    return text;
  };

  return (
    <div className={`rounded-lg border p-3 ${borderCls}`} data-testid="indicator-us-decision-suite">
      <div className="flex items-center gap-1.5 mb-2">
        <Scale3d className="w-4 h-4 text-emerald-400" />
        <span className="text-[10px] font-mono uppercase tracking-wider text-emerald-300">
          US Highway Decision Suite
          <span className="text-[9px] font-bold px-1 py-0.5 rounded bg-emerald-500/25 text-emerald-200 ml-1">DEMO</span>
        </span>
        <span className={`ml-auto text-[9px] font-mono font-bold px-1.5 py-0.5 rounded ${chipCls}`}
              data-testid="decision-suite-status">{status.toUpperCase()}</span>
      </div>

      <div className="rounded border border-slate-800 bg-slate-950/50 p-2 mb-2">
        <div className="flex items-baseline justify-between">
          <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400">CoG-X</span>
          <span className="text-[10px] text-slate-500 font-mono">target {COGX.target}% · safe {COGX.safeLow}-{COGX.safeHigh}%</span>
        </div>
        <div className="flex items-baseline gap-2 mt-0.5">
          <span className="text-2xl font-bold font-mono tabular-nums text-slate-100" data-testid="decision-suite-cogx-pct">
            {cog.cog_x_pct || 0}%
          </span>
          <span className="text-[11px] text-slate-500 font-mono" data-testid="decision-suite-cogx-in">
            {cog.cog_x_in || 0} in from nose
          </span>
        </div>
        <CogXBar cog={cog} />
      </div>

      <div className="rounded border border-slate-800 bg-slate-950/50 p-2 mb-2">
        <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-1">
          <span>Illustrative axle range</span>
          <span className="text-slate-600">min–max</span>
        </div>
        {["steer", "drive", "trailer", "gvw"].map((k) => (
          <DecisionRangeRow key={k} axle={k} r={suite.axle_ranges[k]} />
        ))}
      </div>

      {(suite.reasons || []).length > 0 && (
        <div className={`rounded border px-2 py-1.5 mb-2 text-[10px] ${
          status === "amber" ? "border-amber-500/50 bg-amber-500/10 text-amber-200"
          : "border-rose-500/50 bg-rose-500/10 text-rose-200"}`}
             data-testid="decision-suite-reasons">
          <div className="flex items-start gap-1.5">
            <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
            <div className="space-y-0.5">
              {suite.reasons.map((r, i) => <div key={i}>{r}</div>)}
            </div>
          </div>
        </div>
      )}

      <button type="button" onClick={copyProfile}
              className="w-full text-[11px] font-mono font-semibold py-1.5 rounded border border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-100"
              data-testid="decision-suite-copy-profile-btn">
        Copy Load Profile
      </button>

      <div className="mt-2 text-[9px] italic text-slate-500 border-t border-slate-800 pt-1.5" data-testid="decision-suite-illustrative-label">
        {suite.illustrative_label || DECISION_SUITE_LABEL}
      </div>
    </div>
  );
}

// Pinned right rail: status, payload %, US axle balance (FHWA) OR US Scale Ticket (BETA), safety.
export function TelemetryRail({ plan, strictCtu, usHighway, usDecisionSuite, lastAudit, onOpenAuditor, hideAuditor, truckSpecs }) {
  if (!plan) return (
    <div className="w-[320px] shrink-0 border-l border-slate-800 bg-slate-900/60 flex items-center justify-center text-sm text-slate-600" data-testid="telemetry-rail">
      Run a plan to see live telemetry.
    </div>
  );
  const st = STATUS[plan.status] || STATUS.fits;
  const StIcon = st.Icon;
  const wd = plan.weight_distribution || {};
  const cont = plan.container || {};
  const cap = cont.max_payload_kg || 0;
  const wt = plan.total_weight_kg || 0;
  const wpct = cap ? Math.round((wt / cap) * 100) : 0;
  const over = wt > cap;
  const q = plan.quality;
  return (
    <div className="w-[320px] shrink-0 border-l border-slate-800 bg-slate-900/60 flex flex-col p-3 gap-3 overflow-y-auto min-h-0" data-testid="telemetry-rail">
      {!hideAuditor && (
        <button type="button" onClick={() => lastAudit && onOpenAuditor && onOpenAuditor()} disabled={!lastAudit}
          title={lastAudit ? "Open the report for the last blocked pallet" : "No blocked adds yet — the report appears here after a pallet is rejected"}
          className={`w-full flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left transition-colors ${lastAudit ? "border-blue-500/50 bg-blue-500/10 hover:bg-blue-500/20 text-blue-200" : "border-slate-800 bg-slate-900 text-slate-600 cursor-not-allowed"}`}
          data-testid="telemetry-auditor-btn">
          <span className="flex items-center gap-2">
            <Search className="w-4 h-4" />
            <span className="text-[12px] font-semibold">Placement Auditor</span>
          </span>
          {lastAudit
            ? <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-rose-500/20 text-rose-300" data-testid="telemetry-auditor-badge">Step {lastAudit.blocked_step} · {lastAudit.candidate?.product_code}</span>
            : <span className="text-[9px] font-mono uppercase tracking-wider text-slate-600">idle</span>}
        </button>
      )}

      <div className={`rounded-lg border p-3 ${st.cls}`} data-testid="indicator-status-chip">
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-mono uppercase tracking-wider opacity-80">Container status</span>
          <StIcon className="w-4 h-4" />
        </div>
        <div className="text-lg font-bold font-mono mt-1" data-testid="indicator-status-label">{st.label}</div>
        <div className="flex items-center gap-2 mt-1 text-[11px] font-mono opacity-90">
          <span>{plan.total_pallets} pallets</span>
          {q?.score != null && <span>· quality {q.score}/100</span>}
        </div>
      </div>

      <div className="rounded-lg border border-slate-800 bg-slate-900 p-3" data-testid="indicator-weight-pct">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400">Payload utilisation</span>
          <Gauge className="w-4 h-4 text-slate-500" />
        </div>
        <div className={`text-2xl font-bold font-mono ${over ? "text-rose-400" : "text-slate-100"}`} data-testid="indicator-weight-value">{wpct}%</div>
        <div className="h-2 rounded-full bg-slate-800 overflow-hidden mt-1">
          <div className={`h-full transition-all duration-300 ${over ? "bg-rose-500" : wpct > 80 ? "bg-emerald-500" : "bg-blue-500"}`} style={{ width: `${Math.min(100, wpct)}%` }} />
        </div>
        <div className="mt-1 space-y-0.5 text-[11px] font-mono tabular-nums" data-testid="indicator-weight-values">
          <div className="flex items-center justify-between">
            <span className="text-amber-500" data-testid="indicator-weight-lbs">
              {Math.round(wt * KG_TO_LB).toLocaleString()} <span className="text-slate-500">/</span> {Math.round(cap * KG_TO_LB).toLocaleString()}
              <span className="text-[9px] uppercase tracking-wider opacity-80 ml-1">lbs</span>
            </span>
            {over && <span className="text-rose-400 font-bold text-[10px]">OVER</span>}
          </div>
          <div className="text-sky-500" data-testid="indicator-weight-kg">
            {Math.round(wt).toLocaleString()} <span className="text-slate-500">/</span> {Math.round(cap).toLocaleString()}
            <span className="text-[9px] uppercase tracking-wider opacity-80 ml-1">kg</span>
          </div>
        </div>
        <div className="text-[11px] font-mono text-slate-500 mt-0.5">{plan.utilisation_length_pct}% floor length used</div>
      </div>

      {usDecisionSuite ? (
        <UsDecisionSuiteWidget plan={plan} truckSpecs={truckSpecs} />
      ) : usHighway ? (
        // US Highway Mode: swap the FHWA 60/40 bar for the Law-of-Levers Scale Ticket
        <UsScaleTicket us={plan.us_highway} plan={plan} truckSpecs={truckSpecs} />
      ) : (
      <div className="rounded-lg border border-slate-800 bg-slate-900 p-3" data-testid="indicator-axle-balance">
        <div className="flex items-center gap-1.5 mb-2">
          <Scale3d className="w-4 h-4 text-cyan-400" />
          <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400">US axle balance (FHWA)</span>
        </div>
        <div className="mb-1 flex items-center justify-between">
          <span className="text-[11px] text-slate-300">Front / Rear</span>
          <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${wd.fhwa_ok ? "bg-emerald-500/15 text-emerald-400" : "bg-rose-500/15 text-rose-400"}`}>{wd.fhwa_ok ? "60/40 OK" : "60/40 FAIL"}</span>
        </div>
        <SplitBar leftPct={wd.front_pct} rightPct={wd.rear_pct} ok={wd.fhwa_ok}
          leftLabel={<span className="inline-flex items-center gap-1"><span className="text-slate-400">F</span><WeightLbsKg kg={wd.front_kg} /></span>}
          rightLabel={<span className="inline-flex items-center gap-1"><span className="text-slate-400">R</span><WeightLbsKg kg={wd.rear_kg} /></span>} guideline />
        <div className="mt-3 mb-1 flex items-center justify-between">
          <span className="text-[11px] text-slate-300">Left / Right (from Rear)</span>
          <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${wd.lr_ok ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-400"}`}>{wd.lr_ok ? "Balanced" : "Uneven"}</span>
        </div>
        <SplitBar leftPct={wd.left_pct} rightPct={wd.right_pct} ok={wd.lr_ok}
          leftLabel={<span className="inline-flex items-center gap-1"><span className="text-slate-400">L</span><WeightLbsKg kg={wd.left_kg} /></span>}
          rightLabel={<span className="inline-flex items-center gap-1"><span className="text-slate-400">R</span><WeightLbsKg kg={wd.right_kg} /></span>} />
      </div>
      )}

      {strictCtu && (() => {
        const off = wd.cog_y_offset_in || 0;
        const pct = wd.cog_y_pct || 0;
        const ok = wd.cog_y_ok !== false;
        const side = off > 0.05 ? "Right" : off < -0.05 ? "Left" : "Centre";
        const airbags = wd.center_airbags_required ?? 0;
        return (
          <div className="rounded-lg border border-cyan-800/60 bg-cyan-950/20 p-3" data-testid="indicator-ctu-balance">
            <div className="flex items-center gap-1.5 mb-2">
              <Scale3d className="w-4 h-4 text-cyan-400" />
              <span className="text-[10px] font-mono uppercase tracking-wider text-cyan-300">IMO CTU balance (beta)</span>
            </div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] text-slate-300">Lateral CoG (CoG-Y)</span>
              <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${ok ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-400"}`} data-testid="ctu-cogy-status">{ok ? "\u22645% OK" : ">5% OFF-CENTRE"}</span>
            </div>
            <div className="text-[13px] font-mono text-slate-100" data-testid="ctu-cogy-value">
              {Math.abs(off).toFixed(1)} in {side === "Centre" ? "on centreline" : `${side} of centre`} <span className="text-slate-400">({pct > 0 ? "+" : ""}{pct.toFixed(1)}%)</span>
            </div>
            {!ok && (
              <div className="mt-1 text-[11px] text-amber-300 flex items-start gap-1.5" data-testid="ctu-cogy-warning">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />Lateral CoG is more than 5% off-centre — shift weight toward the {side === "Right" ? "Left" : "Right"} lane.
              </div>
            )}
            <div className="mt-2 flex items-center justify-between border-t border-cyan-900/60 pt-2">
              <span className="text-[11px] text-slate-300">Total Center Airbags Required</span>
              <span className="text-[15px] font-mono font-bold text-cyan-200" data-testid="ctu-airbag-count">{airbags}</span>
            </div>
            <div className="text-[9px] text-slate-500 mt-0.5">Wall-flushed model · one airbag per centre gap &gt; 4 in.</div>
          </div>
        );
      })()}

      {(q?.best_move || plan.securing?.needs_bracing) && (
        <div className="rounded-lg border border-slate-800 bg-slate-900 p-3" data-testid="indicator-safety">
          <div className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-1">Safety &amp; integrity</div>
          {plan.securing?.needs_bracing && (
            <div className="text-[11px] text-orange-300 flex items-start gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />Needs bracing · {(plan.securing.loose_stacks || []).length} stack(s)
            </div>
          )}
          {q?.best_move && <div className="text-[11px] text-blue-300 mt-1">Best move: {q.best_move}</div>}
        </div>
      )}

      {(() => {
        const rows = (plan.product_rows || []).filter(r => r.matched && (r.pallet_count || 0) > 0);
        if (!rows.length) return null;
        const fillerPallets = rows.reduce((s, r) => s + (r.filler_pallet_count || 0), 0);
        return (
          <div className="rounded-lg border border-slate-800 bg-slate-900 p-3" data-testid="load-manifest">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400 flex items-center gap-1.5"><Package className="w-3.5 h-3.5" />Load contents</span>
              {fillerPallets > 0 && (
                <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-orange-500/15 text-orange-300 border border-orange-500/30" data-testid="manifest-filler-total">{fillerPallets} filler plt</span>
              )}
            </div>
            <div className="space-y-1">
              {rows.map((r) => {
                const fc = r.filler_pallet_count || 0;
                const po = (r.pallet_count || 0) - fc;
                return (
                  <div key={r.product_code} className="flex items-center gap-1.5 text-[11px]" data-testid={`manifest-row-${r.product_code}`}>
                    <span className="font-mono text-slate-200 truncate flex-1">{r.product_code}</span>
                    {fc > 0 && (
                      <span className="text-[8px] font-bold px-1 py-0.5 rounded bg-orange-500 text-white shrink-0" data-testid={`manifest-filler-badge-${r.product_code}`}>FILLER</span>
                    )}
                    <span className="font-mono text-slate-400 shrink-0 tabular-nums">
                      {r.pallet_count} plt{fc > 0 && <span className="text-orange-300"> ({po}+{fc})</span>}
                    </span>
                  </div>
                );
              })}
            </div>
            {fillerPallets > 0 && <div className="text-[9px] text-slate-500 mt-1.5">Orange = non-PO (Top-Up / catalog). Format: PO+filler pallets.</div>}
          </div>
        );
      })()}
    </div>
  );
}

// Horizontal tray of UNASSIGNED (deferred) pallets. Appears only when the pool is non-empty.
// Each chip = one product+kind group with a count; drag it onto the Top View (or tap +) to drop
// a pallet back into the active container. Chips keep their identity: PO (Unassigned) / Filler.
export function UnassignedTray({ pool, onReturn, onDiscard, onClearAll, activeLabel }) {
  const entries = Object.entries(pool || {})
    .flatMap(([code, e]) => [
      ...(e.po > 0 ? [{ code, kind: "po", count: e.po, description: e.description || "", ppp: e.ppp || 0 }] : []),
      ...(e.filler > 0 ? [{ code, kind: "filler", count: e.filler, description: e.description || "", ppp: e.ppp || 0 }] : []),
    ]);
  if (!entries.length) return null;
  const total = entries.reduce((s, x) => s + x.count, 0);
  const drag = (e, it) => e.dataTransfer.setData("text/plain", JSON.stringify({
    source: "pool", product_code: it.code, kind: it.kind, ppp: it.ppp, description: it.description,
  }));
  return (
    <div className="h-[112px] shrink-0 border-t border-amber-500/30 bg-amber-950/20 px-4 py-2 flex flex-col" data-testid="unassigned-tray">
      <div className="flex items-center gap-3 mb-1.5 shrink-0">
        <PackageX className="w-4 h-4 text-amber-400" />
        <span className="text-[10px] font-mono uppercase tracking-wider text-amber-300 truncate">
          Unassigned pallets · {total} deferred — drag a chip onto the top view → {activeLabel} (or tap +)
        </span>
        <div className="ml-auto flex items-center gap-2 shrink-0">
          <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/40" data-testid="unassigned-total">{total} plt</span>
          <button type="button" onClick={onClearAll} data-testid="unassigned-clear-all-btn"
            className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-rose-600/80 hover:bg-rose-500 text-white border border-rose-500/50 flex items-center gap-1 transition-colors">
            <Trash2 className="w-2.5 h-2.5" />Clear all
          </button>
        </div>
      </div>
      <div className="flex-1 min-h-0 flex gap-2 overflow-x-auto pb-1">
        {entries.map((it) => (
          <div key={`${it.code}-${it.kind}`} draggable onDragStart={(e) => drag(e, it)}
            data-testid={`unassigned-chip-${it.code}-${it.kind}`}
            className={`relative w-[168px] shrink-0 rounded-lg border p-2 flex flex-col justify-between hover:-translate-y-0.5 transition-all cursor-grab active:cursor-grabbing ${it.kind === "filler" ? "border-orange-500/50 bg-orange-950/30" : "border-slate-700 bg-slate-900"}`}>
            <button type="button" onClick={() => onDiscard(it.code, it.kind)} title="Remove this from Unassigned"
              className="absolute top-1 right-1 h-4 w-4 rounded-full bg-slate-700/80 hover:bg-rose-600 text-slate-300 hover:text-white flex items-center justify-center transition-colors z-10"
              data-testid={`unassigned-discard-${it.code}-${it.kind}`}>
              <X className="w-2.5 h-2.5" />
            </button>
            <div className="pr-5">
              <div className="flex items-center gap-1.5">
                <span className="font-mono text-xs font-bold text-slate-100 truncate">{it.code}</span>
                <span className={`text-[8px] font-bold px-1 py-0.5 rounded shrink-0 ${it.kind === "filler" ? "bg-orange-500 text-white" : "bg-slate-600 text-slate-100"}`}>
                  {it.kind === "filler" ? "FILLER" : "PO"}
                </span>
              </div>
              <div className="text-[10px] text-slate-400 mt-0.5 truncate">{it.description || "—"}</div>
            </div>
            <div className="flex items-center justify-between mt-1">
              <span className="text-[10px] font-mono text-amber-300 tabular-nums" data-testid={`unassigned-count-${it.code}-${it.kind}`}>×{it.count} unassigned</span>
              <button type="button" onClick={() => onReturn(it.code, it.kind)} title={`Return 1 to ${activeLabel}`}
                className="h-5 w-5 rounded-full bg-amber-600 hover:bg-amber-500 text-white flex items-center justify-center" data-testid={`unassigned-return-${it.code}-${it.kind}`}>
                <Plus className="w-3 h-3" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Fixed bottom tray of searchable, draggable catalog product cards.
export function CatalogTray({ catalog, onAdd, activeLabel }) {
  const [q, setQ] = useState("");
  const items = (catalog || [])
    .filter((p) => !q || (p.product_code || "").toLowerCase().includes(q.toLowerCase()) || (p.description || "").toLowerCase().includes(q.toLowerCase()))
    .slice(0, 80);
  const drag = (e, p) => e.dataTransfer.setData("text/plain", JSON.stringify({
    product_code: p.product_code, ppp: p.default_qty_per_pallet || 0, description: p.description || "",
  }));
  return (
    <div className="h-[156px] shrink-0 border-t border-slate-800 bg-slate-900/95 px-4 py-2 flex flex-col" data-testid="catalog-tray">
      <div className="flex items-center gap-3 mb-2 shrink-0">
        <Package className="w-4 h-4 text-blue-400" />
        <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400 truncate">Product catalog · drag a card onto the top view → {activeLabel}</span>
        <div className="ml-auto relative w-64">
          <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search product code or description…" data-testid="catalog-search-input"
            className="w-full h-7 pl-7 pr-2 text-xs rounded-md bg-slate-800 border border-slate-700 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-blue-500" />
        </div>
      </div>
      <div className="flex-1 min-h-0 flex gap-2 overflow-x-auto pb-1">
        {items.length === 0 && <div className="text-xs text-slate-500 flex items-center">No products match “{q}”.</div>}
        {items.map((p) => (
          <div key={p.id || p.product_code} draggable onDragStart={(e) => drag(e, p)} data-testid={`catalog-card-${p.product_code}`}
            className="w-[172px] shrink-0 rounded-lg border border-slate-800 bg-slate-900 p-2 flex flex-col justify-between hover:border-blue-500 hover:-translate-y-0.5 transition-all cursor-grab active:cursor-grabbing">
            <div>
              <div className="font-mono text-xs font-bold text-slate-100 truncate">{p.product_code}</div>
              <div className="text-[10px] text-slate-400 mt-0.5 overflow-hidden leading-tight" style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{p.description || "—"}</div>
            </div>
            <div className="flex items-center justify-between mt-1">
              <span className="text-[10px] font-mono text-slate-500">{p.default_qty_per_pallet ? `${p.default_qty_per_pallet}/plt` : "no ppp"}</span>
              <button type="button" onClick={() => onAdd(p.product_code, p.default_qty_per_pallet || 0, p.description || "")}
                title="Add 1 pallet to active container"
                className="h-5 w-5 rounded-full bg-blue-600 hover:bg-blue-500 text-white flex items-center justify-center" data-testid={`catalog-add-${p.product_code}`}>
                <Plus className="w-3 h-3" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
